/**
 * 自定义视频播放组件 - 美观的设计
 * 参考现代播放器样式，提供清晰的界面和完整功能
 */

import React, { useRef, useState, useEffect } from 'react';
import './VideoPlayer.css';

interface VideoPlayerProps {
  url: string;
  className?: string;
  style?: React.CSSProperties;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ url, className = '', style }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isHovering, setIsHovering] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const hideControlsTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 调试：确认组件被渲染
  useEffect(() => {
  }, [url]);

  // 播放时自动隐藏控件，暂停时显示控件
  useEffect(() => {
    if (hideControlsTimerRef.current) {
      clearTimeout(hideControlsTimerRef.current);
      hideControlsTimerRef.current = null;
    }

    if (isPlaying && !isHovering) {
      // 播放中且鼠标不在控件上，延迟隐藏（3秒）
      hideControlsTimerRef.current = setTimeout(() => {
        if (!isHovering) {
          setShowControls(false);
        }
      }, 3000);
    } else if (!isPlaying) {
      // 暂停时始终显示控件
      setShowControls(true);
    }

    return () => {
      if (hideControlsTimerRef.current) {
        clearTimeout(hideControlsTimerRef.current);
      }
    };
  }, [isPlaying, isHovering]);

  useEffect(() => {
    const video = videoRef.current;
    const container = containerRef.current;
    if (!video) return;

    const updateTime = () => setCurrentTime(video.currentTime);
    const updateDuration = () => {
      setDuration(video.duration);
      setIsLoading(false);
      
      // 检查视频是否有画面轨道（延迟检查，因为metadata加载后可能还需要时间）
      // 对于blob URL，需要更长的等待时间，因为浏览器需要完全解析视频
      const isBlobUrl = url.startsWith('blob:');
      const checkDelay = isBlobUrl ? 2000 : 1000; // blob URL等待2秒，其他等待1秒
      
      setTimeout(() => {
        if (video.videoWidth === 0 && video.videoHeight === 0 && video.duration > 0) {
          // 再次检查，可能还没完全加载
          setTimeout(() => {
            if (video.videoWidth === 0 && video.videoHeight === 0 && video.duration > 0) {
              setHasError(true);
              console.warn('视频没有画面轨道，可能只有音频。视频尺寸:', video.videoWidth, 'x', video.videoHeight, 'URL:', url);
            }
          }, 1000);
        }
      }, checkDelay);
    };
    
    // 禁用右键菜单
    const handleContextMenu = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      return false;
    };
    
    video.addEventListener('timeupdate', updateTime);
    video.addEventListener('loadedmetadata', updateDuration);
    video.addEventListener('play', () => setIsPlaying(true));
    video.addEventListener('pause', () => setIsPlaying(false));
    video.addEventListener('ended', () => setIsPlaying(false));
    video.addEventListener('waiting', () => setIsLoading(true));
    video.addEventListener('canplay', () => setIsLoading(false));
    video.addEventListener('error', () => {
      setHasError(true);
      setIsLoading(false);
      console.error('视频加载错误:', video.error);
    });
    
    // 禁用视频元素的右键菜单（使用capture阶段确保优先处理）
    video.addEventListener('contextmenu', handleContextMenu, { capture: true, passive: false });
    
    // 禁用容器的右键菜单
    if (container) {
      container.addEventListener('contextmenu', handleContextMenu, { capture: true, passive: false });
    }

    return () => {
      video.removeEventListener('timeupdate', updateTime);
      video.removeEventListener('loadedmetadata', updateDuration);
      video.removeEventListener('play', () => setIsPlaying(true));
      video.removeEventListener('pause', () => setIsPlaying(false));
      video.removeEventListener('ended', () => setIsPlaying(false));
      video.removeEventListener('waiting', () => setIsLoading(true));
      video.removeEventListener('canplay', () => setIsLoading(false));
      video.removeEventListener('error', () => {
        setHasError(true);
        setIsLoading(false);
      });
      video.removeEventListener('contextmenu', handleContextMenu, { capture: true });
      if (container) {
        container.removeEventListener('contextmenu', handleContextMenu, { capture: true });
      }
    };
  }, [url]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    
    if (isPlaying) {
      video.pause();
    } else {
      video.play().catch(err => {
        console.error('播放失败:', err);
        setHasError(true);
      });
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    if (!video) return;
    
    const newTime = parseFloat(e.target.value);
    video.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    if (!video) return;
    
    const newVolume = parseFloat(e.target.value);
    video.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const formatTime = (seconds: number): string => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!url) {
    return null;
  }

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div 
      ref={containerRef}
      className={`custom-video-player-wrapper ${className}`} 
      style={style}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
        return false;
      }}
      onMouseEnter={() => {
        setIsHovering(true);
        setShowControls(true);
        // 清除隐藏定时器
        if (hideControlsTimerRef.current) {
          clearTimeout(hideControlsTimerRef.current);
          hideControlsTimerRef.current = null;
        }
      }}
      onMouseLeave={() => {
        setIsHovering(false);
        // 如果正在播放，延迟隐藏控件
        if (isPlaying) {
          if (hideControlsTimerRef.current) {
            clearTimeout(hideControlsTimerRef.current);
          }
          hideControlsTimerRef.current = setTimeout(() => {
            setShowControls(false);
          }, 3000);
        } else {
          // 暂停时保持显示
          setShowControls(true);
        }
      }}
      onMouseMove={() => {
        // 鼠标移动时显示控件并重置隐藏定时器
        setIsHovering(true);
        setShowControls(true);
        if (hideControlsTimerRef.current) {
          clearTimeout(hideControlsTimerRef.current);
          hideControlsTimerRef.current = null;
        }
        // 如果正在播放，设置延迟隐藏（只有在鼠标移出后才隐藏）
        // 这里不需要立即设置，因为鼠标还在移动，会在onMouseLeave中处理
      }}
    >
      <video
        ref={videoRef}
        src={url}
        className="custom-video-element"
        preload={url.startsWith('blob:') ? 'auto' : 'metadata'} // blob URL使用auto预加载，确保能正确解析
        playsInline
        onClick={togglePlay}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          e.nativeEvent.stopImmediatePropagation();
          return false;
        }}
        controls={false}
        controlsList="nodownload noplaybackrate nofullscreen"
        onLoadedData={() => {
          // 当数据加载完成后，重置错误状态（如果之前误判）
          const video = videoRef.current;
          if (video && video.videoWidth > 0 && video.videoHeight > 0) {
            setHasError(false);
          }
        }}
      />

      {/* 加载指示器 */}
      {isLoading && (
        <div className="video-loading">
          <div className="loading-spinner"></div>
          <span>加载中...</span>
        </div>
      )}

      {/* 错误提示 */}
      {hasError && (
        <div className="video-error">
          <div className="error-icon">⚠️</div>
          <div className="error-text">
            {videoRef.current?.videoWidth === 0 && videoRef.current?.videoHeight === 0
              ? '此文件只有音频轨道，没有视频画面'
              : '视频加载失败'}
          </div>
          {!(videoRef.current?.videoWidth === 0 && videoRef.current?.videoHeight === 0) && (
            <button className="error-retry" onClick={() => {
              setHasError(false);
              setIsLoading(true);
              videoRef.current?.load();
            }}>重试</button>
          )}
        </div>
      )}

      {/* 中央播放按钮 */}
      {!isPlaying && !hasError && !isLoading && (
        <div className="video-center-play" onClick={togglePlay}>
          <div className="play-button-large">
            <svg viewBox="0 0 24 24" fill="white" width="60" height="60">
              <path d="M8 5v14l11-7z"/>
            </svg>
          </div>
        </div>
      )}

      {/* 自定义控制栏 */}
      {!hasError && (
        <div className={`custom-video-controls ${showControls ? 'visible' : 'hidden'}`}>
          {/* 进度条 */}
          <div className="video-progress-container" onClick={(e) => {
            const video = videoRef.current;
            if (!video || !duration) return;
            const rect = e.currentTarget.getBoundingClientRect();
            const percent = (e.clientX - rect.left) / rect.width;
            video.currentTime = percent * duration;
            setCurrentTime(video.currentTime);
          }}>
            <div className="video-progress-background">
              <div 
                className="video-progress-filled" 
                style={{ width: `${progressPercent}%` }}
              />
              <input
                type="range"
                min="0"
                max={duration || 0}
                value={currentTime}
                onChange={handleSeek}
                className="video-progress-slider"
              />
            </div>
          </div>

          {/* 控制按钮栏 */}
          <div className="video-controls-bar">
            <div className="video-controls-left">
              {/* 播放/暂停按钮 */}
              <button className="video-control-btn play-pause-btn" onClick={togglePlay}>
                {isPlaying ? (
                  <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                )}
              </button>

              {/* 时间显示 */}
              <span className="video-time">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
            </div>

            <div className="video-controls-right">
              {/* 音量控制 */}
              <button className="video-control-btn volume-btn" onClick={toggleMute}>
                {isMuted || volume === 0 ? (
                  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                  </svg>
                ) : volume < 0.5 ? (
                  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                    <path d="M18.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9H5z"/>
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                  </svg>
                )}
              </button>

              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={isMuted ? 0 : volume}
                onChange={handleVolumeChange}
                className="video-volume"
              />

              {/* 设置按钮 */}
              <button className="video-control-btn settings-btn" title="设置">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94L14.4 2.81c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.07.62-.07.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/>
                </svg>
              </button>

              {/* 全屏按钮 */}
              <button 
                className="video-control-btn fullscreen-btn" 
                onClick={() => {
                  const container = containerRef.current;
                  if (container) {
                    if (document.fullscreenElement) {
                      document.exitFullscreen();
                    } else {
                      container.requestFullscreen();
                    }
                  }
                }}
                title="全屏"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;